// Author: Sunil Jain
// File: src/components/BulkInviteModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal, Box, Typography, TextField, Button, Stack, CircularProgress, Alert,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import logger from '../utils/logger';
import authFetch from '../utils/authFetch';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 550 },
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: '90vh',
  overflowY: 'auto'
};

const userCategories = [
  { value: 'patient', label: 'Patient' },
  { value: 'physician_qhp', label: 'Physician (QHP)' },
  { value: 'clinical_staff', label: 'Clinical Staff' },
  { value: 'tenant_admin', label: 'Tenant Admin' },
];

const getCsvColumns = (category) => {
    switch (category) {
        case 'patient':
            return 'email, first_name, last_name, date_of_birth, mrn';
        default: // For staff
            return 'email, first_name, last_name';
    }
};

const sendBulkInviteAPI = async ({ file, emailSubject, emailBody, targetCategory }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email_subject', emailSubject);
    formData.append('email_body', emailBody);
    formData.append('target_category', targetCategory);
    
    // NOTE: This endpoint name is generalized. The backend router must be updated to match.
    // The previous endpoint '/api/admin/users/bulk-invite-staff' was too specific.
    return authFetch('/api/admin/users/bulk-invite', {
        method: 'POST',
        body: formData,
    });
};

const BulkInviteModal = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [targetCategory, setTargetCategory] = useState('');
  const [emailSubject, setEmailSubject] = useState("Your Invitation to Join Our Team");
  const [emailBody, setEmailBody] = useState("Hello {first_name},\n\nYou have been invited to join our organization. Please click the link below to complete your registration.\n\n{registration_link}\n\nThank you!");
  const [validationError, setValidationError] = useState('');

  const { mutate, isLoading, isError, error: mutationError, reset: resetMutation } = useMutation({
    mutationFn: sendBulkInviteAPI,
    onSuccess: (data) => {
      logger.info("Bulk invite job successfully created:", data);
      onClose(true); // Pass true to indicate success
    },
    onError: (error) => {
      logger.error("Bulk invite failed:", error);
    },
  });

  // Reset form state when modal opens.
  // The dependency array is key to fixing the infinite loop.
  useEffect(() => {
    if (open) {
      setFile(null);
      setTargetCategory('');
      setEmailSubject("Your Invitation to Join Our Team");
      setEmailBody("Hello {first_name},\n\nYou have been invited to join our organization. Please click the link below to complete your registration.\n\n{registration_link}\n\nThank you!");
      setValidationError('');
      resetMutation();
    }
  }, [open, resetMutation]); // `resetMutation` has a stable identity from `useMutation`

  const handleFileChange = (event) => {
    setValidationError(''); // Clear previous validation errors
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
          setValidationError('Invalid file type. Please upload a .csv file.');
          setFile(null);
      } else {
          setFile(selectedFile);
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setValidationError('');
    
    if (!file || !targetCategory) {
      setValidationError('Please select a category and upload a CSV file.');
      return;
    }
    
    mutate({ file, emailSubject, emailBody, targetCategory });
  };

  return (
    <Modal open={open} onClose={() => onClose(false)} aria-labelledby="bulk-invite-modal-title">
      <Box sx={style}>
        <Typography id="bulk-invite-modal-title" variant="h6" component="h2" gutterBottom>
          Bulk Invite Users
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="user-category-select-label">User Category</InputLabel>
                <Select
                    labelId="user-category-select-label"
                    value={targetCategory}
                    label="User Category"
                    onChange={(e) => {
                        const newCategory = e.target.value;
                        setTargetCategory(newCategory);
                        setFile(null);
                        setValidationError('');
                        if (isError) resetMutation();

                        if (newCategory === 'patient') {
                            setEmailSubject("Your Invitation to EmpowerAi");
                            setEmailBody("Hello {first_name},\n\nYou have been invited to join our care program. Please click the link below to get started and complete your registration.\n\n{registration_link}\n\nThank you!");
                        } else {
                            setEmailSubject("Your Invitation to Join Our Team");
                            setEmailBody("Hello {first_name},\n\nYou have been invited to join our organization. Please click the link below to complete your registration.\n\n{registration_link}\n\nThank you!");
                        }
                    }}

                >
                    {userCategories.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {targetCategory && (
                <Alert severity="info">
                    Upload a CSV with columns: <strong>{getCsvColumns(targetCategory)}</strong>.
                    <br />
                    Use placeholders like <code>{'{first_name}'}</code> and <code>{'{registration_link}'}</code> in the email.
                </Alert>
            )}

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={!targetCategory || isLoading}
            >
              Upload CSV File
              <input type="file" accept=".csv" hidden onChange={handleFileChange} />
            </Button>
            {file && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '-8px !important' }}>
                    <CheckCircleIcon color="success" fontSize="small" /> {file.name}
                </Typography>
            )}

            <TextField
              label="Email Subject"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              disabled={!targetCategory || isLoading}
              required
            />
            <TextField
              label="Email Body"
              fullWidth
              multiline
              rows={6}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              disabled={!targetCategory || isLoading}
              required
              helperText="Use placeholders for personalization."
            />
            
            {(validationError || isError) && (
              <Alert severity="error">
                {validationError || mutationError?.detail || "An error occurred. Please check your inputs and try again."}
              </Alert>
            )}
            
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => onClose(false)} disabled={isLoading}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={isLoading || !file}>
                {isLoading ? <CircularProgress size={24} /> : 'Start Bulk Invitation'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};

export default BulkInviteModal;